import { useCallback, useLayoutEffect, useRef } from "react";

// Keeps an anchor element visually stationary across a re-render that
// changes layout above or below it (e.g. a filter toggle collapsing a
// long list). The browser preserves the scroll container's scrollTop in
// pixels, not the position of the element the user interacted with - so
// when content shrinks, the clicked control jumps under the cursor.
//
// Usage: call the returned capture function with the anchor element
// right before the state update. After React commits the new layout
// (useLayoutEffect runs pre-paint, so there is no visible flicker) the
// hook scrolls the nearest scrollable ancestor by the anchor's viewport
// delta, pinning it in place.
//
// If the container cannot scroll far enough (page shrank below one
// viewport of content past the anchor) the compensation is partial -
// scrollTop clamps at its bounds, which is the best any approach can do.

function scrollParentOf(el: HTMLElement): Element | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return document.scrollingElement;
}

export function useScrollAnchor(): (el: HTMLElement | null) => void {
  const pending = useRef<{ el: HTMLElement; top: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = pending.current;
    if (!anchor) return;
    pending.current = null;
    if (!anchor.el.isConnected) return;
    const delta = anchor.el.getBoundingClientRect().top - anchor.top;
    if (delta === 0) return;
    const parent = scrollParentOf(anchor.el);
    if (parent) parent.scrollTop += delta;
  });

  return useCallback((el: HTMLElement | null) => {
    if (el) pending.current = { el, top: el.getBoundingClientRect().top };
  }, []);
}
